/**
 * useURLStateManager Hook
 *
 * React hook wrapper for URLStateManager class.
 * Provides easy access to URL state management in React components.
 *
 * Usage:
 * function MyComponent() {
 *   const urlStateManager = useURLStateManager();
 *
 *   // Get current filters
 *   const filters = urlStateManager.getFilters();
 *
 *   // Update filter
 *   urlStateManager.updateFilter('location', 'Denver');
 *
 *   // Subscribe to changes
 *   useEffect(() => {
 *     return urlStateManager.subscribe((filters) => {
 *       console.log('Filters changed:', filters);
 *     });
 *   }, [urlStateManager]);
 * }
 *
 * Location: apps/web/src/hooks/useURLStateManager.js
 * Created: 2025-10-31
 * Part of: MVP 1.9 - Adaptive Search & Filter System
 */

import { useSearchParams } from 'react-router-dom';
import { useMemo } from 'react';
import URLStateManager from '../services/search/URLStateManager';

/**
 * Hook to access URL state manager
 * @returns {URLStateManager} - URL state manager instance
 */
export function useURLStateManager() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Memoize URLStateManager instance to prevent recreation on every render
  // Only recreate if searchParams or setSearchParams change (which they shouldn't)
  const urlStateManager = useMemo(() => {
    return new URLStateManager(searchParams, setSearchParams);
  }, [searchParams, setSearchParams]);

  return urlStateManager;
}

export default useURLStateManager;
